import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt, faSpinner, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import Tooltip from "@mui/material/Tooltip";
import { faArrowPointer, faHandPointer, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

import { setThemePreference } from "../../state/reducers/themeReducer";
import { useTouchLayout } from "../Hooks/useTouchLayout";
import { RootState } from '../../state/store';
import { logoutUser } from '../../state/actions/authActions';
import { useAppDispatch } from '../../state/hooks';
import { pageOf, routeChainAt, satisfiesRouteRequirements } from '../../utilities/helpers/routeAccess';
import { UserDto } from '../../DTOs/User/UserDto';
import { RoleConfig } from '../../DTOs/Settings/RoleConfigDto';
import { AppRouteConfig, createAppRoutes } from "../../utilities/appRoutes";
import { customSectionName, roleLabel, routeLabel, sectionName, sectionOverrides, viewName } from "../../i18n/pageNames";
import { getRouteIcon } from "../../utilities/routeIcons";
import { ModalContextType, useGlobalModal } from "../Contexts/GlobalModalContext";
import MineClock from './MineClock';
import { HeaderZones } from './HeaderZones';
import { HeaderLanguage } from './HeaderLanguage';
import { findActiveNav, isNavItemActive, menuItemKey } from './navActive';
import { useDrillBlastModules } from '../Hooks/useDrillBlastSettings';

interface MenuItem {
  label: string;
  to?: string;
  onClick?: () => void; // instead of linking
  icon?: IconDefinition;
  subItems?: MenuItem[];
}

const NAV_ITEM_BASE =
  "w-full px-3 py-2 border-l-4 text-left text-theme-primary-contrast transition hover:bg-theme-primary-hover";
const NAV_LINK = `${NAV_ITEM_BASE} flex items-center gap-2`;
const NAV_CATEGORY = `${NAV_ITEM_BASE} flex justify-between items-center cursor-pointer`;
const NAV_STRIP_IDLE = "border-transparent";
const NAV_STRIP_ACTIVE = "border-theme-primary-contrast bg-theme-primary-hover font-semibold";
const NAV_STRIP_TRAIL = "border-theme-primary-contrast/40";

const routeName = (route: AppRouteConfig, roleConfig: RoleConfig | null): string =>
  (route.section && customSectionName(sectionOverrides(roleConfig), route.section))
  || (route.view && viewName(route.view))
  || (route.section && sectionName(route.section))
  || (route.label && routeLabel(route.label))
  || route.path;

// Check user has permission for a route
const isRouteAllowed = (route: AppRouteConfig, user: UserDto | null) => {
  if (user?.isSuperAdmin) return true;
  if (!route.protected) return true;
  return satisfiesRouteRequirements(route, user);
};

// Recursively convert route config to menu structure
const buildMenu = (
  routes: AppRouteConfig[],
  user: UserDto | null,
  roleConfig: RoleConfig | null,
  showModal: ModalContextType["showModal"],
  parentPath = ''
): MenuItem[] => {
  const menu: MenuItem[] = [];

  for (const route of routes) {
    if (!route.path || route.path === '*' || route.ignore) continue;
    if (!isRouteAllowed(route, user)) continue;

    const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/'); // Normalize slashes
    const hasChildren = route.children && route.children.length > 0;
    const subItems = hasChildren ? buildMenu(route.children!, user, roleConfig, showModal, fullPath) : [];

    const isGroupOnly = !route.element && subItems.length > 0;

    // Note: a pure grouping route (no element of its own, e.g. "Geology" or "Admin") derives its
    // visibility entirely from whether any child survived the access check above, so it is skipped when
    // none did - not merely when it has no children declared.
    if (!route.element && !route.action && subItems.length === 0) continue;

    const menuItem: MenuItem = {
      label: routeName(route, roleConfig),
      icon: route.icon ?? getRouteIcon(fullPath),
      ...(route.action
        ? {
          onClick: () => route.action!(showModal)
        }
        : isGroupOnly ? {} : { to: fullPath }),
      ...(subItems.length > 0 ? { subItems } : {})
    };

    menu.push(menuItem);
  }

  return menu;
};

const TOUCH_VIEWS = new Set(["BlastHoleDrilling", "BlastPattern", "RcDrilling", "ExplosivesStore", "DrillConsumables"]);

const Header: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isAuthLoading, user, roleConfig } = useSelector((s: RootState) => s.authReducer);
  const themePref = useSelector((s: RootState) => s.theme.preference);
  const { showModal } = useGlobalModal();
  const { pathname } = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  // Categories default to open when they contain the current page; this records explicit user toggles.
  const [collapseOverrides, setCollapseOverrides] = useState<Record<string, boolean>>({});
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const openMenu = () => setMenuOpen(true);

  const closeMenu = () => {
    menuButtonRef.current?.focus();
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const toggleTheme = () => {
    dispatch(setThemePreference(themePref === "dark" ? "light" : "dark"));
  };

  const touchLayout = useTouchLayout();
  const touchLabel = touchLayout.on ? t("header.touchOn") : t("header.touchOff");

  const themeIcon = themePref === "dark" ? faMoon : faSun;
  const themeLabel = themePref === "dark" ? t("header.themeDark") : t("header.themeLight");

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logoutUser());
    closeMenu();
  };

  const telemetryEnabled = roleConfig?.telemetryEnabled;
  const drillBlastModules = useDrillBlastModules();
  const routes = useMemo(() => createAppRoutes({ telemetryEnabled, drillBlastModules }), [telemetryEnabled, drillBlastModules]);
  const menuItems = useMemo(() => buildMenu(routes, user, roleConfig, showModal), [routes, user, roleConfig, showModal]);
  const { activeTo, trail } = useMemo(() => findActiveNav(menuItems, pathname), [menuItems, pathname]);

  const isExpanded = (key: string) => collapseOverrides[key] ?? trail.has(key);

  const toggleCategory = (key: string) => {
    const next = !isExpanded(key);
    setCollapseOverrides(prev => ({ ...prev, [key]: next }));
  };

  const renderMenuItems = (items: MenuItem[], depth = 0, parentKey = '') => {
    return (
      <ul className={`${depth > 0 ? "pl-3 py-2 mt-1 mb-2 border-l border-theme-border ml-2" : ""}`}>
        {items.map(item => {
          const itemKey = menuItemKey(parentKey, item.label);
          const isActive = isNavItemActive(item.to, activeTo);

          return (
            <li key={itemKey} className={`${depth > 0 ? "" : "border-t border-theme-border first:border-t-0"}`}>
              {item.subItems ? (
                <>
                  <button
                    type="button"
                    onClick={() => toggleCategory(itemKey)}
                    aria-expanded={isExpanded(itemKey)}
                    className={clsx(NAV_CATEGORY, isActive ? NAV_STRIP_ACTIVE : trail.has(itemKey) ? NAV_STRIP_TRAIL : NAV_STRIP_IDLE)}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                      {item.label}
                    </span>
                    <span className="ml-3 shrink-0">{isExpanded(itemKey) ? "▾" : "▸"}</span>
                  </button>
                  {isExpanded(itemKey) && renderMenuItems(item.subItems, depth + 1, itemKey)}
                </>
              ) : item.onClick ? (
                <button
                  onClick={() => {
                    item.onClick?.();
                    closeMenu();
                  }}
                  className={clsx(NAV_LINK, NAV_STRIP_IDLE)}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                  {item.label}
                </button>
              ) : (
                <Link
                  to={item.to!}
                  onClick={closeMenu}
                  aria-current={isActive ? "page" : undefined}
                  className={clsx(NAV_LINK, isActive ? NAV_STRIP_ACTIVE : NAV_STRIP_IDLE)}
                >
                  {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth className="opacity-80" />}
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const openView = pageOf(routeChainAt(routes, pathname))?.view;
  const currentViewDisplay = openView ? viewName(openView) : '';
  const userRole = user?.roles[0];
  const role = userRole && roleLabel(userRole);

  useEffect(() => {
    if (!menuOpen) return;
    closeButtonRef.current?.focus();

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = original;
    };
  }, [menuOpen]);

  return (
    <>
      <header className="bg-theme-primary text-theme-primary-contrast border-b border-theme-border relative transition duration-150 sticky top-0 z-30 overflow-hidden">
        <nav className="flex items-center justify-between px-2 sm:px-4 py-3 compact:py-1 gap-2">
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              aria-label={t("header.toggleMenu")}
              className="flex flex-col justify-center items-center gap-1 w-8 h-8 compact:w-6 compact:h-6 p-1 sm:mr-2 cursor-pointer"
            >
              <span className="block w-full h-1 compact:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 compact:h-0.5 bg-theme-primary-contrast rounded" />
              <span className="block w-full h-1 compact:h-0.5 bg-theme-primary-contrast rounded" />
            </button>
            <h2 className="font-semibold text-sm compact:text-[0.8rem] truncate max-w-[8rem] xs:max-w-none md:text-lg">{currentViewDisplay}</h2>
          </div>

          <div className="flex-none space-x-3 lg:space-x-4 flex lg:gap-2 items-center transition">
            <HeaderZones />
            {<MineClock />}
            {openView && TOUCH_VIEWS.has(openView) && (
              <Tooltip title={touchLabel} arrow>
                <button
                  type="button"
                  onClick={() => touchLayout.set(touchLayout.on ? "off" : "on")}
                  className="flex items-center justify-center transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer"
                  aria-label={touchLabel}
                  aria-pressed={touchLayout.on}
                >
                  <FontAwesomeIcon icon={touchLayout.on ? faHandPointer : faArrowPointer} className="!w-6 !h-6 compact:!w-4 compact:!h-4 sm:!w-5 sm:!h-5" />
                </button>
              </Tooltip>
            )}
            <Tooltip title={themeLabel} arrow>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center justify-center transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer"
                aria-label={themeLabel}
              >
                <FontAwesomeIcon icon={themeIcon} className="!w-6 !h-6 compact:!w-4 compact:!h-4 sm:!w-5 sm:!h-5" />
              </button>
            </Tooltip>
            <HeaderLanguage />

            {isAuthLoading ? (
              <span className={`transition opacity-${isAuthLoading ? "100" : "0"}`}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </span>
            ) : (
              isAuthenticated && (
                <Link
                  to="/account"
                  className={`inline-flex items-start font-semibold hover:text-theme-primary-contrast-hover transition duration-150 opacity-${isAuthLoading ? "0" : "100"}`}
                >
                  <span className="!hidden sm:!inline compact:text-[0.8rem] truncate max-w-[12rem] lg:max-w-none">{user?.name ?? t("header.user")}</span>
                  <FontAwesomeIcon icon={faUser} className="!block sm:!hidden !w-6 !h-6 compact:!w-4 compact:!h-4" />
                  {role && (
                    <span className="relative -top-1 ml-0.5 font-normal leading-none hidden lg:inline opacity-80 text-[0.45rem] tracking-tight sm:text-[0.65rem] sm:tracking-normal compact:text-[0.55rem]">
                      {role}
                    </span>
                  )}
                </Link>
              )
            )}

            {isAuthenticated ? (
              <button onClick={handleLogout} className="transition duration-150 hover:text-theme-primary-contrast-hover cursor-pointer">
                <span className="!hidden sm:!inline compact:text-[0.8rem]">{t("header.logout")}</span>
                <FontAwesomeIcon icon={faSignOutAlt} className="!block sm:!hidden !w-6 !h-6 compact:!w-4 compact:!h-4" />
              </button>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)} className="transition duration-150 hover:text-theme-primary-contrast-hover">
                {t("header.login")}
              </Link>
            )}
          </div>
        </nav>
      </header>

      <div
        className={`fixed inset-0 z-50 transition duration-150 ${menuOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-150 ${menuOpen ? "opacity-100" : "opacity-0"}`}
          onClick={closeMenu}
        />

        <aside
          className={`absolute top-0 left-0 h-full w-[22rem] max-w-[90vw] bg-theme-primary text-theme-primary-contrast border-theme-border transition-transform duration-150 flex flex-col ${menuOpen ? "translate-x-0 border-r shadow-2xl shadow-theme-border" : "-translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-2 sm:px-4 py-4 compact:py-1 border-b border-theme-primary-contrast shrink-0">
            <button
              ref={closeButtonRef}
              onClick={closeMenu}
              className="text-3xl compact:text-2xl leading-none hover:text-theme-primary-contrast-hover transition cursor-pointer"
              aria-label={t("header.closeMenu")}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3 className="font-semibold text-lg compact:text-[0.8rem]">{t("header.menu")}</h3>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain">
            {renderMenuItems(menuItems)}
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
